import type { AIEdit } from "../types/edit";

/**
 * Replace the first occurrence of `search`, treating `replacement` as a literal
 * string. Passing a function keeps `$&`, `` $` `` and `$1` in AI-suggested text
 * from being interpreted as substitution patterns.
 */
export function replaceLiteral(text: string, search: string, replacement: string): string {
    return text.replace(search, () => replacement);
}

/** Matches the display gutter `numberLines` adds, e.g. `  12→`. */
const GUTTER = /^\s*\d+→/;

/**
 * Prefix each line with its 1-based number so the AI has something to aim
 * `insert_lines` at. The numbers are for reading only — they are stripped back
 * off anything the AI quotes, see `stripGutter`.
 */
export function numberLines(markdown: string): string {
    const lines = markdown.split("\n");
    const width = String(lines.length).length;
    return lines
        .map((line, i) => `${String(i + 1).padStart(width, " ")}→${line}`)
        .join("\n");
}

/**
 * Remove the gutter from text the AI echoed back. Only strips when every
 * non-blank line carries one, so a document line that merely starts with a
 * number survives untouched.
 */
export function stripGutter(text: string): string {
    if (!text) return text;

    const lines = text.split("\n");
    const filled = lines.filter(line => line.trim() !== "");
    if (filled.length === 0 || !filled.every(line => GUTTER.test(line))) return text;

    return lines.map(line => line.replace(GUTTER, "")).join("\n");
}

/** Clamp a 1-based line number to a valid splice index for `lines`. */
export function insertionIndex(line: number, lines: string[]): number {
    if (!Number.isFinite(line)) return lines.length;
    return Math.max(0, Math.min(Math.trunc(line) - 1, lines.length));
}

/**
 * Apply an edit to markdown. The preview and the accept action both go through
 * here so the diff that is shown is always the diff that gets applied.
 */
export function applyEdit(markdown: string, edit: AIEdit): string {
    if (edit.type === "replace" && edit.original) {
        return replaceLiteral(markdown, edit.original, edit.new);
    }

    if (edit.type === "insert" && edit.line !== undefined) {
        const lines = markdown.split("\n");
        lines.splice(insertionIndex(edit.line, lines), 0, edit.new);
        return lines.join("\n");
    }

    return markdown;
}

/**
 * Escape characters that would otherwise break out of the diff markup the
 * preview renders through rehype-raw. `>` is left alone so blockquotes inside a
 * diff still render as blockquotes.
 */
export function escapeForHtml(text: string): string {
    return text.replace(/&/g, "&amp;").replace(/</g, "&lt;");
}
